// Settings panel — opened from the toolkit's SETTINGS button and
// persists until the user taps its close (X) button or toggles the
// toolkit again. Same canvas/texture/mesh pattern as toolkit-panel:
// paint() is state-driven, pickButton() decodes a UV hit to a button
// id.
//
// Initial setting: Dominant Hand (LEFT / RIGHT) — flips which hand
// owns the panel-summon face buttons + shape/select-action dial vs
// undo/redo + action dial.

import * as THREE from "three";
import type { Handedness } from "../interaction/tool";
import {
  TEXT_DIM,
  TEXT_LIGHT,
  glassFont,
  paintGlassButton,
  paintGlassCloseButton,
  paintGlassPanel,
  setupPanelTexture,
} from "./glass";

const CANVAS_W = 1024;
const CANVAS_H = 1024;
const PANEL_W = 0.36;
const PANEL_H = 0.36;
const MARGIN = 40;

// Button IDs. "close" is the X in the corner; "dom_left" / "dom_right"
// are the two halves of the Dominant Hand toggle; passthrough / recenter /
// help are the environment actions moved here from the toolkit.
export const SETTINGS_BUTTON_IDS = [
  "close",
  "dom_left",
  "dom_right",
  "passthrough",
  "recenter",
  "help",
] as const;
export type SettingsButtonId = (typeof SETTINGS_BUTTON_IDS)[number];

const canvas = document.createElement("canvas");
canvas.width = CANVAS_W;
canvas.height = CANVAS_H;
const texture = new THREE.CanvasTexture(canvas);
setupPanelTexture(texture);

const material = new THREE.MeshBasicMaterial({
  map: texture,
  transparent: true,
  side: THREE.DoubleSide,
  depthTest: false,
  depthWrite: false,
});

export const settingsPanelMesh = new THREE.Mesh(
  new THREE.PlaneGeometry(PANEL_W, PANEL_H),
  material,
);
settingsPanelMesh.renderOrder = 950;
settingsPanelMesh.visible = false;

interface ButtonRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

// Close button — small square in the top-right corner.
const CLOSE_SIZE = 100;
function closeRect(): ButtonRect {
  return {
    x: CANVAS_W - MARGIN - CLOSE_SIZE,
    y: MARGIN,
    w: CLOSE_SIZE,
    h: CLOSE_SIZE,
  };
}

// Environment actions row (Passthrough / Recenter / Help) — three equal
// buttons under the "Environment" header near the top of the panel.
function actionRects(): {
  passthrough: ButtonRect;
  recenter: ButtonRect;
  help: ButtonRect;
} {
  const gap = 20;
  const top = 230;
  const h = 160;
  const w = (CANVAS_W - 2 * MARGIN - 2 * gap) / 3;
  return {
    passthrough: { x: MARGIN, y: top, w, h },
    recenter: { x: MARGIN + (w + gap), y: top, w, h },
    help: { x: MARGIN + 2 * (w + gap), y: top, w, h },
  };
}

// Dominant-hand toggle: two equal halves sitting under the section
// header, in the lower half of the panel.
function domToggleRects(): {
  left: ButtonRect;
  right: ButtonRect;
} {
  const gap = 20;
  const top = 600;
  const h = 220;
  const w = (CANVAS_W - 2 * MARGIN - gap) / 2;
  return {
    left: { x: MARGIN, y: top, w, h },
    right: { x: MARGIN + w + gap, y: top, w, h },
  };
}

export interface SettingsPanelState {
  dominantHand: Handedness;
  passthroughOn: boolean;
  hot: SettingsButtonId | null;
}

export function paintSettingsPanel(state: SettingsPanelState): void {
  const ctx = canvas.getContext("2d")!;
  paintGlassPanel(ctx, CANVAS_W, CANVAS_H);

  // Panel title.
  ctx.fillStyle = TEXT_LIGHT;
  ctx.font = glassFont("700 76px");
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("Settings", MARGIN + 8, MARGIN + 8);

  // Close button (X in the top-right corner).
  const close = closeRect();
  paintGlassCloseButton(ctx, close.x, close.y, close.w, close.h, state.hot === "close");

  // Environment section header + actions.
  ctx.fillStyle = TEXT_DIM;
  ctx.font = glassFont("600 40px");
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("Environment", MARGIN + 8, 170);

  const act = actionRects();
  // Font sized to fit the longest label ("Recenter Canvas") in a third-
  // width button; kept uniform across the three so they read as a set.
  const ACTION_FONT = 32;
  paintGlassButton(ctx, act.passthrough.x, act.passthrough.y, act.passthrough.w, act.passthrough.h, {
    label: "Passthrough",
    active: state.passthroughOn,
    hot: state.hot === "passthrough",
    fontPx: ACTION_FONT,
  });
  paintGlassButton(ctx, act.recenter.x, act.recenter.y, act.recenter.w, act.recenter.h, {
    label: "Recenter Canvas",
    hot: state.hot === "recenter",
    fontPx: ACTION_FONT,
  });
  paintGlassButton(ctx, act.help.x, act.help.y, act.help.w, act.help.h, {
    label: "Help",
    hot: state.hot === "help",
    fontPx: ACTION_FONT,
  });

  // Dominant-hand section header.
  ctx.fillStyle = TEXT_DIM;
  ctx.font = glassFont("600 40px");
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("Dominant hand", MARGIN + 8, 500);

  const dom = domToggleRects();
  const drawHalf = (
    rect: ButtonRect,
    id: "dom_left" | "dom_right",
    label: string,
  ) => {
    const active =
      (id === "dom_left" && state.dominantHand === "left") ||
      (id === "dom_right" && state.dominantHand === "right");
    paintGlassButton(ctx, rect.x, rect.y, rect.w, rect.h, {
      label,
      active,
      hot: state.hot === id,
    });
  };
  drawHalf(dom.left, "dom_left", "Left");
  drawHalf(dom.right, "dom_right", "Right");

  texture.needsUpdate = true;
}

export function pickSettingsButton(
  uvX: number,
  uvY: number,
): SettingsButtonId | null {
  const x = uvX * CANVAS_W;
  const y = (1 - uvY) * CANVAS_H;
  const close = closeRect();
  if (
    x >= close.x &&
    x <= close.x + close.w &&
    y >= close.y &&
    y <= close.y + close.h
  ) {
    return "close";
  }
  const dom = domToggleRects();
  if (
    x >= dom.left.x &&
    x <= dom.left.x + dom.left.w &&
    y >= dom.left.y &&
    y <= dom.left.y + dom.left.h
  ) {
    return "dom_left";
  }
  if (
    x >= dom.right.x &&
    x <= dom.right.x + dom.right.w &&
    y >= dom.right.y &&
    y <= dom.right.y + dom.right.h
  ) {
    return "dom_right";
  }
  const act = actionRects();
  for (const id of ["passthrough", "recenter", "help"] as const) {
    const r = act[id];
    if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) return id;
  }
  return null;
}
